import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ClockAlert, Plus } from "lucide-react";
import { toast } from "sonner";

const istToday = () => new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() + 330 * 60000 - n * 86400000).toISOString().slice(0, 10);

// Employee self-service: "I forgot to check in / out" — raise a correction for
// HR / Super Admin. Rules (enforced server-side): within 7 days, max 3 a
// month, not for a payroll-locked month, corrected times still follow the
// normal grace / late rules.
export default function AttendanceCorrections({ prefillDate }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ date: prefillDate || istToday(), check_in_time: "", check_out_time: "", reason: "" });
  const { data } = useQuery({ queryKey: ["myCorrections"], queryFn: async () => (await api.get("/my/attendance/corrections")).data });

  const submit = useMutation({
    mutationFn: async () => (await api.post("/my/attendance/corrections", f)).data,
    onSuccess: () => {
      toast.success("Request sent to HR");
      qc.invalidateQueries({ queryKey: ["myCorrections"] }); setOpen(false);
      setF({ date: istToday(), check_in_time: "", check_out_time: "", reason: "" });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const withdraw = useMutation({
    mutationFn: async (id) => (await api.post(`/my/attendance/corrections/${id}/withdraw`)).data,
    onSuccess: () => { toast.success("Request withdrawn"); qc.invalidateQueries({ queryKey: ["myCorrections"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const items = data?.items || [];
  const left = data ? Math.max(0, data.monthly_cap - data.used_this_month) : null;
  const valid = f.date && (f.check_in_time || f.check_out_time) && f.reason.trim().length >= 10;

  return (
    <Card className="p-4 border-slate-200 shadow-none mb-6">
      <div className="flex flex-wrap items-center gap-3">
        <ClockAlert className="h-5 w-5 text-teal" />
        <div className="flex-1 min-w-[200px]">
          <p className="font-semibold text-navy">Missed a check-in or check-out?</p>
          <p className="text-xs text-slate-500">Request a correction within {data?.window_days ?? 7} days.
            {left !== null && ` ${left} of ${data.monthly_cap} requests left for ${new Date().toLocaleDateString("en-IN", { month: "long" })} dates.`} Late arrival rules still apply to the corrected time.</p>
        </div>
        <Button className="bg-navy hover:bg-navy-700" onClick={() => setOpen(true)} disabled={left === 0} data-testid="request-correction">
          <Plus className="h-4 w-4 mr-1" /> Request correction
        </Button>
      </div>

      {items.length > 0 && (
        <div className="mt-4 space-y-2">
          {items.slice(0, 8).map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-slate-100 px-3 py-2 text-sm">
              <span className="font-mono-i text-teal w-24">{r.date}</span>
              <span className="text-slate-600">In {r.requested_check_in || "—"} · Out {r.requested_check_out || "—"}</span>
              <span className="flex-1" />
              {r.status === "Approved" && r.applied && <span className="text-xs text-slate-500">Day now: {r.applied.status_after}</span>}
              <StatusBadge status={r.status} />
              {r.status === "Pending" && <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => withdraw.mutate(r.id)}>Withdraw</Button>}
              {r.status === "Rejected" && r.decision_remarks && <span className="w-full text-xs text-red-600">{r.decision_remarks}</span>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Request attendance correction</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Date</Label><Input type="date" className="mt-1" value={f.date} min={daysAgo(data?.window_days ?? 7)} max={istToday()} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Actual check-in</Label><Input type="time" className="mt-1" value={f.check_in_time} onChange={(e) => setF({ ...f, check_in_time: e.target.value })} /></div>
              <div><Label>Actual check-out</Label><Input type="time" className="mt-1" value={f.check_out_time} onChange={(e) => setF({ ...f, check_out_time: e.target.value })} /></div>
            </div>
            <p className="text-xs text-slate-400">Fill only the time you missed. Leave the other blank if it was recorded correctly.</p>
            <div><Label>Reason *</Label><Textarea rows={3} className="mt-1" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="e.g. Phone battery died; reached the lab at 09:05 — Sunita (front desk) can confirm." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={!valid || submit.isPending} onClick={() => submit.mutate()}>Send request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
